import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  inject,
  input,
  output,
  signal,
  OnDestroy,
  DestroyRef,
  model,
  computed,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgTemplateOutlet } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  Observable,
  pairwise,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { SearchService } from '../../../..//ui-components/lib/search-field/search.service';
import { FilterDropdownComponent } from '../../../..//ui-components/lib/filter-dropdown/filter-dropdown.component';
import { FilterDropdownService } from '../../../..//ui-components/lib/filter-dropdown/filter-dropdown.service';
import { PaginatorComponent } from '../../../..//ui-components/lib/paginator/paginator.component';
import { StatComponent } from '../../../..//ui-components/lib/stat/stat.component';
import { TableComponent } from '../../../..//ui-components/lib/table/table.component';
import { TreeTableComponent } from '../../../../ui-components/lib/tree-table/tree-table.component';
import { TreeTableRow } from '../../../../ui-components/lib/tree-table/tree-table.model';
import { SearchFieldComponent } from '../../../..//ui-components/lib/search-field/search-field.component';
import { FilterOption } from '../../../..//ui-components/lib/filter-dropdown/filter-dropdown.model';
import {
  ParentTableColumns,
  TableColumns,
  TableFilterTab,
  filterQueryParamName,
} from '../../../../ui-components/lib/table/table.model';
import { StepStatusEnum } from '../../enums/step-status';
import { Insight } from '../../models/insight.model';
import { SearchEnum } from '../../enums/search.enum';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '../../constants/pagination.constants';
import { Meta } from '../../models/api-response.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChipFilter,
  ChipFilterValue,
} from '../chips-filter/chips-filter.model';
import { ChipsFilterComponent } from '../chips-filter/chips-filter.component';
import { FilteredTablePayload, SortColumn } from './filtered-table.model';
import { InsightsPanelComponent } from '../insights-panel/insights-panel.component';
import { injectFloatingLayout } from '../../helpers/system-tabs-layout/system-tabs-layout';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../constants/feature-flags.constants';
import { MoneyPipe } from '../../../../ui-components/pipes/money/money.pipe';

@Component({
  selector: 'lib-filtered-table',
  imports: [
    PaginatorComponent,
    TableComponent,
    TreeTableComponent,
    StatComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    FilterDropdownComponent,
    SearchFieldComponent,
    MoneyPipe,
    NgTemplateOutlet,
    InsightsPanelComponent,
    ChipsFilterComponent,
  ],
  templateUrl: './filtered-table.component.html',
  styleUrls: ['./filtered-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilteredTableComponent implements OnInit, OnDestroy {
  protected readonly parallelStepStatus = StepStatusEnum;
  private readonly router = inject(Router);
  private readonly featureFlags = inject(FeatureFlagsService);
  /** Stat cards use the system's compact size even when the table isn't floating (e.g. inside a details tab). */
  protected readonly isNewTableUi = computed(() =>
    this.featureFlags.getFeatureFlagValue(FEATURE_FLAGS.TABLE.ui_enhancement)
  );
  protected readonly systemDefaultColor = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  private readonly filterService = inject(FilterDropdownService);
  private readonly searchService = inject(SearchService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly floating = injectFloatingLayout();

  protected readonly searchInputWidth = SearchEnum.searchInputWidth;

  pageSizeOptions = input<number[]>(DEFAULT_PAGE_SIZE_OPTIONS);
  parentColumns = input<ParentTableColumns[]>([]);
  enableRowClick = input<boolean>(true);

  insights = input<Insight[]>([]);
  tableTitle = input<string>();
  headerStartTemplate = input<TemplateRef<unknown> | null>(null);
  titleClass = input<string>('');
  lastColumnAlignment = input<string>('');
  columns = input.required<TableColumns[]>();
  dataSource = input.required<unknown[]>();
  totalItems = input<number>(0);
  isLoadingData = input<boolean>(false);
  tableId = input.required<string>();
  tableFilterTabs = input<TableFilterTab[]>([]);
  tableColumnTemplates = input.required<
    {
      identifier?: string;
      template: TemplateRef<unknown>;
    }[]
  >();
  filterOptions = input<FilterOption[]>([]);
  filterMapping = input<Record<string, string>>({});
  meta = input<Meta>();
  searchPlaceholder = input<string>();
  allowViewToggle = input<boolean>(false);
  /** Grid / list as labelled pill tabs instead of the icon toggle (see lib-table-actions). */
  showGridListTabs = input<boolean>(false);
  /** Rows of filter chips under the toolbar; kept in the URL and sent with the other filters. */
  chipFilters = input<ChipFilter[]>([]);
  isTableExportable = input<boolean>(true);
  tableActionSectionRef = input<TemplateRef<unknown> | null>(null);
  isCalendarViewEnabled = input<boolean>(false);
  calendarTemplateRef = input<TemplateRef<unknown> | null>(null);
  applyOnStreamChanges = input.required<() => Observable<any>>();
  showSearch = input<boolean>(true);
  isTreeTable = input(false);
  treeLabelTemplate = input<TemplateRef<unknown> | null>(null);
  /** Tree tables own their search, so the host can hand it to the toolbar here. */
  treeSearchTemplateRef = input<TemplateRef<unknown> | null>(null);
  rowTestIdPrefix = input('tree-table-row');
  toggleTestIdPrefix = input('tree-table-toggle');
  expandAriaLabel = input('tree_table_expand_button');
  collapseAriaLabel = input('tree_table_collapse_button');
  tableClass = input(
    'min-h-[calc(100vh-31.5rem)] h-auto w-full whitespace-nowrap'
  );
  emptyStateTemplateRef = input<TemplateRef<unknown> | null>(null);
  galleryCardTemplateRef = input<TemplateRef<unknown> | null>(null);
  galleryClass = input<string>(
    'w-full gap-lg grid grid-cols-[repeat(auto-fill,minmax(15.25rem,1fr))] mt-2xl'
  );
  hideTopSpacing = input<boolean>(false);
  isGalleryView = model<boolean>(false);

  payload = model.required<FilteredTablePayload>();

  submitExport = output<FormGroup>();
  streamSucceeds = output<any>(); // This is can be any because used to emit the stream after debouncing and distinctUntilChanged
  handleApiCall = output<FilteredTablePayload>();
  handleViewIconClick = output<any>();
  toggleExpanded = output<string | number>();
  handleRowClick = output<unknown>();

  /** Tables_new_design: lib-table shows the search in its toolbar row (tree tables keep it in this header). */
  protected readonly searchInToolbar = computed(
    () => this.isNewTableUi() && this.showSearch() && !this.isTreeTable()
  );
  /**
   * UI_Version_2 drops the list title above the table, on floating pages and inside details tabs
   * alike; the page title or the tab already names the list.
   */
  protected readonly showTableTitle = computed(
    () => !!this.tableTitle() && !this.systemDefaultColor()
  );
  protected readonly showTableHeader = computed(
    () =>
      !!this.headerStartTemplate() ||
      this.showTableTitle() ||
      (this.showSearch() && !this.searchInToolbar())
  );
  protected readonly treeCellTemplates = computed(() =>
    this.tableColumnTemplates().map(column => column.template)
  );
  protected readonly treeDataSource = computed(
    () => this.dataSource() as TreeTableRow[]
  );

  activeSort = signal<SortColumn>({
    key: '',
    value: 'asc',
  });
  pressedKeyType = signal<string>('');
  isMoreThanThreeCharacters = signal(false);
  activeTableFilterTab = signal<TableFilterTab>(this.tableFilterTabs()[0]);
  initialFilterValues = signal<Record<string, string | string[]>>({});
  chipFilterValue = signal<ChipFilterValue>({});
  /** Set when the Filters dropdown's "Clear all" is applied, so the chips clear with it. */
  private clearChipsOnApply = false;

  tabChanges$ = new Subject<TableFilterTab>();
  searchForm = new FormGroup({
    search: new FormControl(''),
  });
  get searchControl() {
    return this.searchForm.get('search') as FormControl;
  }
  get searchValue() {
    return this.searchControl?.value || '';
  }

  ngOnInit(): void {
    this.handleInitialParams();
    const initialSearch = this.getSearchFromUrl();
    this.handleSearchKeyword(initialSearch);
    this.isMoreThanThreeCharacters.set(
      this.searchService.isMinLength(initialSearch)
    );
    this.streamDebouncedObservable();
    this.handleApiCall.emit({ ...this.getApiCallPayload() });
  }

  handleInitialParams() {
    const initialParams = this.activatedRoute.snapshot.queryParams as Record<
      string,
      string | string[]
    >;
    this.initializeFiltersFromUrl(initialParams);
    this.chipFilterValue.set(this.chipValuesFromParams(initialParams));

    // Handle initial table Filter query param
    const initialTab = this.filterService.initializeTableFilterTab(
      initialParams,
      filterQueryParamName,
      this.tableFilterTabs()
    );
    if (initialTab) {
      this.activeTableFilterTab.set(initialTab);
    }
  }

  handleSearchKeyword(searchKeyword: string) {
    this.searchControl?.setValue(searchKeyword);
  }

  streamDebouncedObservable() {
    const streams: Observable<unknown>[] = [
      this.tabChanges$
        .asObservable()
        .pipe(startWith(this.activeTableFilterTab())),
      this.searchControl.valueChanges.pipe(startWith(this.searchValue)),
    ];
    combineLatest(streams)
      .pipe(
        debounceTime(305),
        distinctUntilChanged(
          ([prevTab, prevSearch], [currentTab, currentSearch]) =>
            (prevTab as TableFilterTab)?.key ===
              (currentTab as TableFilterTab)?.key &&
            prevSearch === currentSearch
        ),
        pairwise(),
        switchMap(res => {
          return this.handleStreamCalledObservable(res);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => this.streamSucceeds.emit(res),
      });
  }

  private handleStreamCalledObservable(
    res: [unknown[], unknown[]]
  ): Observable<void> {
    this.getApiCallPayload({ page: 1 });
    const [previous, current] = res;
    const previousTab = previous[0] as TableFilterTab;
    const currentTab = current[0] as TableFilterTab;

    if (previousTab?.key !== currentTab?.key) {
      return this.applyOnStreamChanges()?.() ?? EMPTY;
    }

    return this.getSearchReturnedObservable(current[1] as string);
  }

  private getApiCallPayload(
    updatedPayload?: Partial<FilteredTablePayload>
  ): FilteredTablePayload {
    const filters = this.getFiltersFromUrl();
    const payload = {
      page: this.meta()?.current_page ?? 1,
      size: this.meta()?.per_page ?? DEFAULT_PAGE_SIZE,
      search: this.searchService.checkSearchLength(this.searchValue),
      filters,
      sortColumn: this.activeSort(),
      tab: this.activeTableFilterTab() ?? this.tableFilterTabs()[0],
      ...updatedPayload,
    };
    this.payload.update(() => ({ ...payload }));
    return payload;
  }

  private getFiltersFromUrl(): Record<string, string | string[]> {
    const initialParams = this.activatedRoute.snapshot.queryParams;
    return this.filterService.getFiltersFromQueryParams(
      initialParams,
      this.filterMapping()
    );
  }

  /* Search */
  getSearchReturnedObservable(term: string): Observable<void> {
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      return this.applyOnStreamChanges()?.() ?? EMPTY;
    }

    return EMPTY;
  }

  handleClearSearch() {
    this.searchControl?.setValue('');
    this.searchService.clearSearch();
  }

  private getSearchFromUrl(): string {
    return this.searchService.currentQuery();
  }

  /* Filters */
  async onApplyFilters(filters: Record<string, string | string[]>) {
    const currentQueryParams = this.activatedRoute.snapshot.queryParams;
    const queryParams = this.filterService.mergeFiltersWithQueryParams(
      filters,
      this.filterOptions(),
      currentQueryParams
    );
    if (this.clearChipsOnApply) {
      this.clearChipsOnApply = false;
      this.chipFilters().forEach(filter => delete queryParams[filter.key]);
      this.chipFilterValue.set({});
    }

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      replaceUrl: true,
    });

    this.handleApiCall.emit({ ...this.getApiCallPayload() });
  }

  private initializeFiltersFromUrl(params: Record<string, string | string[]>) {
    const filterValues = this.filterService.queryParamsToFilters(
      params,
      this.filterOptions()
    );
    this.initialFilterValues.set(filterValues);
  }

  onClearAllFilters() {
    this.clearChipsOnApply = true;
  }

  /* Chips filters */
  async onChipFiltersChange(value: ChipFilterValue) {
    this.chipFilterValue.set(value);
    const queryParams: Record<string, string | string[]> = {
      ...this.activatedRoute.snapshot.queryParams,
    };
    this.chipFilters().forEach(filter => {
      const selected = value[filter.key] ?? [];
      if (selected.length) {
        queryParams[filter.key] = selected.join(',');
      } else {
        delete queryParams[filter.key];
      }
    });

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      replaceUrl: true,
    });

    this.handleApiCall.emit({ ...this.getApiCallPayload({ page: 1 }) });
  }

  private chipValuesFromParams(
    params: Record<string, string | string[]>
  ): ChipFilterValue {
    const value: ChipFilterValue = {};
    this.chipFilters().forEach(filter => {
      const param = params[filter.key];
      const raw = Array.isArray(param) ? param.join(',') : (param ?? '');
      const selected = raw.split(',').filter(item => item !== '');
      if (selected.length) {
        value[filter.key] = selected;
      }
    });
    return value;
  }

  handleClearFilters(): Promise<boolean> {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.initialFilterValues.set({
      tab: queryParams['tab'] as string,
      [filterQueryParamName]: queryParams[filterQueryParamName] as string,
    });

    return this.filterService.resetFiltersAndQueryParams(
      this.router,
      this.activatedRoute,
      queryParams,
      false
    );
  }

  async handleTableFilterTab(tab: TableFilterTab) {
    await this.handleClearFilters();
    this.activeTableFilterTab.set(tab);
    this.tabChanges$.next(tab);

    this.handleClearSearch();
  }

  /* Pagination */
  handlePagination($event: number) {
    this.handleApiCall.emit({ ...this.getApiCallPayload({ page: $event }) });
  }

  changeSize($event: number) {
    this.handleApiCall.emit({
      ...this.getApiCallPayload({ size: $event, page: 1 }),
    });
  }

  /* Table Actions */
  sortColumn() {
    this.handleApiCall.emit({
      ...this.getApiCallPayload(),
    });
  }

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set({
        key: '',
        value: 'asc',
      });
    }

    this.sortColumn();
  }

  /* Action List Actions */
  submitExportForm(exportForm: FormGroup) {
    this.submitExport.emit(exportForm);
  }

  ngOnDestroy(): void {
    this.filterService.resetState();
  }
}
